import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, Download, Pencil, PhoneCall, Search, Trash2 } from 'lucide-react';
import { contactsAPI } from '../../api/client';
import { useTenant } from '../../context/TenantContext';
import { apiError } from '../../lib/host';
import CallDialog from './CallDialog';
import { ComingSoon, ErrorBanner, formatDate, Modal, PageHeading, saveDownload, StatCards, useBasic } from './ui';

const STATUSES = [['new', 'New'], ['hot', 'Hot'], ['warm', 'Warm'], ['cold', 'Cold']];
const STATUS_LABEL = Object.fromEntries(STATUSES);
const SOURCES = ['Website', 'Google Business', 'Phone Ads', 'Digital Ads', 'Referrals', 'Walk-in', 'Phone call', 'Other'];
const EMPTY = { name: '', phone: '', email: '', source: '', status: 'new', follow_up_on: '', notes: '' };

function ContactForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, v ?? ''])) } : EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) {
      setError('Add a phone number or an email address.');
      return;
    }
    const body = {
      name: form.name.trim(), phone: form.phone, email: form.email, source: form.source,
      status: form.status, follow_up_on: form.follow_up_on || null, notes: form.notes,
    };
    setSaving(true);
    setError('');
    try {
      const res = initial ? await contactsAPI.update(initial.id, body) : await contactsAPI.create(body);
      onSaved(res.data, initial ? 'updated' : 'added');
    } catch (err) {
      setError(apiError(err, 'Could not save the contact'));
      setSaving(false);
    }
  };

  return (
    <Modal
      title={initial ? `Edit ${initial.name}` : 'Add contact'}
      onClose={onClose}
      footer={<>
        <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
        <button type="submit" form="contact-form" className="primary-action" disabled={saving}>{saving ? 'Saving…' : initial ? 'Save changes' : 'Add contact'}</button>
      </>}
    >
      <form id="contact-form" className="form-grid" onSubmit={save}>
        <ErrorBanner message={error} />
        <label className="field span-2">Name *<input required maxLength={255} value={form.name} onChange={set('name')} autoFocus /></label>
        <label className="field">Phone<input type="tel" maxLength={32} value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" /></label>
        <label className="field">Email<input type="email" maxLength={255} value={form.email} onChange={set('email')} placeholder="name@example.com" /></label>
        <label className="field">Source
          <select value={form.source} onChange={set('source')}>
            <option value="">—</option>
            {[...new Set([...SOURCES, form.source].filter(Boolean))].map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="field">Status
          <select value={form.status} onChange={set('status')}>{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </label>
        <label className="field">Follow-up date<input type="date" value={form.follow_up_on} onChange={set('follow_up_on')} /></label>
        <label className="field span-2">Notes<textarea rows={3} maxLength={5000} value={form.notes} onChange={set('notes')} /></label>
        <p className="field-hint span-2">A phone number or an email address is required.</p>
      </form>
    </Modal>
  );
}

export default function ContactsPage() {
  const { notify, upgrade, plan } = useBasic();
  const { can } = useTenant();
  const canWrite = can('contacts.write');
  const canCall = can('calls.make');
  const [calling, setCalling] = useState(null);
  const [params, setParams] = useSearchParams();
  const [contacts, setContacts] = useState(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null); // null = closed, {} = new, contact = edit
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filters = useCallback(() => ({ ...(query && { q: query }), ...(status && { status }) }), [query, status]);
  const filtered = Boolean(query || status);

  const load = useCallback(() => {
    contactsAPI.list(filters())
      .then((res) => { setContacts(res.data.items); setTotal(res.data.total); setError(''); })
      .catch((err) => setError(apiError(err, 'Could not load contacts')));
    contactsAPI.stats().then((res) => setStats(res.data)).catch(() => {});
  }, [filters]);

  useEffect(load, [load]);

  useEffect(() => {
    if (params.get('new') && canWrite) {
      setEditing({});
      setParams({}, { replace: true });
    }
  }, [params, setParams, canWrite]);

  const saved = (contact, verb) => {
    setEditing(null);
    notify(`${contact.name} ${verb}.`);
    load();
  };

  const remove = async (contact) => {
    if (!window.confirm(`Delete ${contact.name}? This can't be undone.`)) return;
    try {
      await contactsAPI.remove(contact.id);
      notify(`${contact.name} deleted.`);
      load();
    } catch (err) {
      setError(apiError(err, 'Could not delete the contact'));
    }
  };

  const exportCsv = async () => {
    if (!total) {
      notify(filtered ? 'No contacts match the current search to export.' : 'No contacts to export yet. Add a contact first.');
      return;
    }
    try {
      saveDownload(await contactsAPI.exportCsv(filters()), 'contacts');
      notify(`Exported ${total} contact${total === 1 ? '' : 's'}.`);
    } catch (err) {
      setError(apiError(err, 'Could not export contacts'));
    }
  };

  return (
    <section className="page-view">
      <PageHeading title="Contacts" subtitle="Keep customer details, lead sources, and follow-up status in one place.">
        <button className="secondary-action" onClick={exportCsv}><Download size={14} />Export</button>
        {canWrite && <button className="primary-action" onClick={() => setEditing({})}><span>＋</span>Add contact</button>}
      </PageHeading>

      <ErrorBanner message={error} />

      <StatCards stats={[
        ['Total contacts', stats?.total, 'All contacts'],
        ['New leads', stats?.new_this_month, 'Added this month'],
        ['Hot leads', stats?.hot, 'Need follow-up'],
        ['Follow-ups due', stats?.follow_ups_due, 'Today or overdue'],
      ]} />

      <div className="page-toolbar">
        <div className="page-search"><Search size={15} /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone or email..." aria-label="Search contacts" /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="">All statuses</option>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <section className="panel page-table-panel">
        <div className="panel-heading">
          <div><h2>Contacts overview</h2><small className="table-subtitle">{contacts === null ? 'Loading…' : `Showing ${contacts.length} of ${total} contact${total === 1 ? '' : 's'}`}</small></div>
        </div>
        <div className="table-scroll">
          <table className="full-page-table">
            <thead><tr><th>Contact</th><th>Phone</th><th>Email</th><th>Source</th><th>Follow-up</th><th>Status</th>{canWrite && <th>Action</th>}</tr></thead>
            <tbody>
              {contacts?.map((c) => (
                <tr key={c.id}>
                  <td><b className="cell-strong">{c.name}</b>{c.notes && <small className="cell-note">{c.notes}</small>}</td>
                  <td>{c.phone || '—'}</td>
                  <td>{c.email || '—'}</td>
                  <td>{c.source || '—'}</td>
                  <td>{formatDate(c.follow_up_on)}</td>
                  <td><span className={`lead-tag ${c.status}`}>{STATUS_LABEL[c.status] || c.status}</span></td>
                  {canWrite && (
                    <td>
                      <div className="row-actions">
                        {canCall && c.phone && <button className="row-action call" onClick={() => setCalling(c)}><PhoneCall size={12} />Call</button>}
                        <button className="row-action" onClick={() => setEditing(c)}><Pencil size={12} />Edit</button>
                        <button className="row-action danger" onClick={() => remove(c)}><Trash2 size={12} />Delete</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {contacts?.length === 0 && (
                <tr><td colSpan={canWrite ? 7 : 6} className="empty-state">
                  {filtered ? 'No contacts match your search.' : <>No contacts yet.{canWrite && <> <button className="link-button" onClick={() => setEditing({})}>Add your first contact</button></>}</>}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="page-bottom-grid">
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Quick actions</h2></div>
          <div className="page-action-list">
            {canWrite && (
              <button onClick={() => setEditing({})}>
                <span className="action-dot dot-0">＋</span><span><b>Add contact</b><small>Save a new customer or lead</small></span><ArrowRight size={14} />
              </button>
            )}
            <button onClick={() => notify('Importing contacts from a CSV file is coming soon.')}>
              <span className="action-dot dot-1">↑</span><span><b>Import contacts</b><ComingSoon /></span><ArrowRight size={14} />
            </button>
            <button onClick={exportCsv}>
              <span className="action-dot dot-2">↓</span><span><b>Export contacts</b><small>Download a CSV of the current list</small></span><ArrowRight size={14} />
            </button>
          </div>
        </section>
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Plan access</h2><span className="active-pill"><i />{plan}</span></div>
          <p className="access-copy">Contacts are stored in your company's own database. Only people in your company can see them.</p>
          <button className="upgrade-full" onClick={upgrade}>Review subscription <ArrowRight size={14} /></button>
        </section>
      </div>

      {editing && <ContactForm initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={saved} />}
      {calling && (
        <CallDialog contact={calling} onClose={() => setCalling(null)}
          onPlaced={() => { setCalling(null); notify(`Calling your phone now. Answer it to be connected to ${calling.name}.`); }} />
      )}
    </section>
  );
}
